import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

interface DaySleep {
  day: string;
  thisWeek: number | null;
  lastWeek: number | null;
}

interface SleepChartProps {
  data?: DaySleep[];
  avgNapHours?: number;
  goodSleepHours?: number; // threshold for the dashed reference line
  labels?: { thisWeek?: string; lastWeek?: string };
  reverseLegend?: boolean; // when true, show lastWeek entry before thisWeek in the legend
}

const DEFAULT_LABELS = { thisWeek: "This week", lastWeek: "Last week" };

const makeTooltip = (labels: { thisWeek: string; lastWeek: string }) =>
  ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-[8px] px-3 py-2 shadow-md text-xs font-inter">
        <p className="font-semibold text-[#1F1F1F] mb-1 text-[10px] tracking-wide">{String(label).slice(0, 2)}</p>
        {payload.map((p: any) => (
          <div key={p.name} className="flex items-center gap-1.5 mt-0.5">
            <span
              className="w-2 h-2 rounded-full inline-block flex-shrink-0"
              style={{ background: p.color }}
            />
            <span style={{ color: p.color }} className="font-medium">
              {p.name === "thisWeek" ? labels.thisWeek : labels.lastWeek} · {p.value != null ? `${p.value}hr` : "—"}
            </span>
          </div>
        ))}
      </div>
    );
  };

function getDynamicYAxis(data: DaySleep[], goodSleepHours: number, targetTickCount = 5) {
  const allValues: number[] = [];
  data.forEach(({ thisWeek, lastWeek }) => {
    if (thisWeek != null) allValues.push(thisWeek);
    if (lastWeek != null) allValues.push(lastWeek);
  });

  if (allValues.length === 0) {
    return { domain: [0, 8] as [number, number], ticks: [0, 2, 4, 6, 8] };
  }

  // Include goodSleepHours in range calculation only when real data exists
  allValues.push(goodSleepHours);

  const rawMin = Math.min(...allValues);
  const rawMax = Math.max(...allValues);

  // Pick a "nice" hour step so tick labels stay clean (1h, 2h, …) rather
  // than the arithmetic-mean step (which produced values like 6.8h, 8.3h).
  const NICE_STEPS = [1, 2, 3, 5];
  const range = Math.max(rawMax - rawMin, 1);
  const targetStep = range / (targetTickCount - 1);
  const step =
    NICE_STEPS.find((s) => s >= targetStep) ?? NICE_STEPS[NICE_STEPS.length - 1];

  // Snap the domain to multiples of `step`, then expand until we have at
  // least `targetTickCount - 1` intervals so the axis isn't cramped.
  let domainMin = Math.max(0, Math.floor(rawMin / step) * step);
  let domainMax = Math.ceil(rawMax / step) * step;
  while ((domainMax - domainMin) / step < targetTickCount - 1) {
    domainMax += step;
    if (
      (domainMax - domainMin) / step < targetTickCount - 1 &&
      domainMin >= step
    ) {
      domainMin -= step;
    }
  }

  const ticks: number[] = [];
  for (let v = domainMin; v <= domainMax + 1e-9; v += step) {
    ticks.push(parseFloat(v.toFixed(1)));
  }

  return { domain: [domainMin, domainMax] as [number, number], ticks };
}

const SKELETON_DATA: DaySleep[] = [
  { day: "Mo", thisWeek: null, lastWeek: null },
  { day: "Tu", thisWeek: null, lastWeek: null },
  { day: "We", thisWeek: null, lastWeek: null },
  { day: "Th", thisWeek: null, lastWeek: null },
  { day: "Fr", thisWeek: null, lastWeek: null },
  { day: "Sa", thisWeek: null, lastWeek: null },
  { day: "Su", thisWeek: null, lastWeek: null },
];

const EMPTY_DOMAIN: [number, number] = [0, 8];
const EMPTY_TICKS = [0, 2, 4, 6, 8];

const SleepChart: React.FC<SleepChartProps> = ({
  data,
  avgNapHours,
  goodSleepHours = 7,
  labels,
  reverseLegend = false,
}) => {
  const mergedLabels = { ...DEFAULT_LABELS, ...(labels ?? {}) };
  const CustomTooltip = makeTooltip(mergedLabels);

  const legendEntries = [
    { color: "#1A2B5F", label: mergedLabels.thisWeek },
    { color: "#A8C4E8", label: mergedLabels.lastWeek },
  ];
  if (reverseLegend) legendEntries.reverse();

  const hasData = data != null && data.some(d => d.thisWeek != null || d.lastWeek != null);
  const { domain, ticks } = hasData
    ? getDynamicYAxis(data!, goodSleepHours)
    : { domain: EMPTY_DOMAIN, ticks: EMPTY_TICKS };

  const chartData = hasData ? data! : SKELETON_DATA;

  const makeIsolatedDot = (dataKey: keyof DaySleep, color: string, radius: number) =>
    (props: any) => {
      const { cx, cy, index } = props;
      const curr = chartData[index]?.[dataKey];
      const prev = index > 0 ? chartData[index - 1]?.[dataKey] : null;
      const next = index < chartData.length - 1 ? chartData[index + 1]?.[dataKey] : null;
      if (curr != null && prev == null && next == null) {
        return <circle cx={cx} cy={cy} r={radius} fill={color} />;
      }
      return null;
    };

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Sleep
        </span>
        <div className="flex items-center gap-3">
          {legendEntries.map((e) => (
            <div key={e.label} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: e.color }} />
              <span className="text-[11px] text-[#1F1F1F] font-inter">{e.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div className="flex-1 relative">
        <ResponsiveContainer width="100%" height={140}>
          <LineChart
            key={String(hasData)}
            data={chartData}
            margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
          >
            <CartesianGrid vertical={false} stroke="#F3F4F6" strokeDasharray="0" />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              dy={6}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              tickFormatter={(v) => `${v}h`}
              domain={domain}
              ticks={ticks}
              allowDataOverflow
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Good sleep reference line */}
            <ReferenceLine
              y={goodSleepHours}
              stroke="#6DD0CF"
              strokeDasharray="4 4"
              strokeWidth={0.5}
            />

            <Line
              type="monotone"
              dataKey="lastWeek"
              stroke="#A8C4E8"
              strokeWidth={2}
              dot={makeIsolatedDot("lastWeek", "#A8C4E8", 4)}
              activeDot={{ r: 4, fill: "#A8C4E8", strokeWidth: 0 }}
              connectNulls
              isAnimationActive={true}
              animationBegin={0}
              animationDuration={1000}
              animationEasing="ease-out"
            />
            <Line
              type="monotone"
              dataKey="thisWeek"
              stroke="#1A2B5F"
              strokeWidth={2.5}
              dot={makeIsolatedDot("thisWeek", "#1A2B5F", 5)}
              activeDot={{ r: 5, fill: "#1A2B5F", strokeWidth: 0 }}
              connectNulls
              isAnimationActive={true}
              animationBegin={200}
              animationDuration={1000}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>

      </div>

      {/* Footer */}
      {avgNapHours != null && (
        <div className="text-[12px] 3xl:text-[14px] text-[#8D8D8D] font-inter text-center mt-1">
          Avg nap in {mergedLabels.thisWeek.toLowerCase()} :{" "}
          <span className="font-semibold text-[#1F1F1F]">{avgNapHours}hr</span>
        </div>
      )}
    </div>
  );
};

export default SleepChart;